/**
 * DD objective metric block (Phase C.1 — real persisted sources, no LLM call).
 *
 * Assembles the read-only "Company Snapshot" metrics for a completed-DD report
 * from the two real, already-persisted sources — never fabricated, never zero-
 * padded, and tagged with provenance so the report is honest about where each
 * number came from:
 *
 *   • `profile`  — founder self-reported `startup_profiles` columns.
 *   • `deck`     — LLM-extracted at score time, persisted in
 *                  `score_event.input_payload.richExtraction.data` (NOT a new
 *                  call — we read what was already stored). Carries a confidence.
 *
 * A metric is included ONLY when its real source has a meaningful value, so an
 * absent metric reflects an exhausted source, not a shortcut. Metrics that need
 * a fresh extraction pass over data-room documents are deferred to Phase C.2.
 */

import { formatCompact } from '@/lib/utils';

export type DDMetricSource = 'profile' | 'deck';

export interface DDMetric {
  key: string;
  label: string;
  /** Display-ready, formatted value. */
  value: string;
  source: DDMetricSource;
  /** Extraction confidence (0–100) for `deck` metrics. */
  confidence?: number;
}

/** Founder-reported columns the block reads from `startup_profiles`. */
export interface DDMetricsProfileSource {
  monthly_revenue?: number | null;
  user_count?: number | null;
  growth_rate?: number | null;
  team_size?: number | null;
  market_size?: number | null;
  funding_target?: number | null;
}

/** The persisted `richExtraction` shape we read (loose — it's JSON in input_payload). */
export interface DDRichExtraction {
  data?: {
    unitEconomics?: {
      cac?: number | null;
      ltv?: number | null;
      ltvCacRatio?: number | null;
      paybackMonths?: number | null;
      grossMargin?: number | null;
      netMargin?: number | null;
      burnRate?: number | null;
      runway?: string | null;
    } | null;
    marketData?: {
      tam?: number | null;
      sam?: number | null;
      som?: number | null;
      marketGrowthRate?: number | null;
    } | null;
  } | null;
  quality?: { overallConfidence?: number | null } | null;
}

/**
 * The extraction inside a score_event `input_payload`. The key differs by
 * scoring path: process-submission writes `richExtraction`, ai-score and
 * investor-analyze-deck write `extraction`. Reading only `richExtraction` left
 * the DD metrics blank for every event scored on the other two paths. (The
 * investor no-grant shape from `investor_score_events` is already normalised to
 * `richExtraction` server-side, by `score_event_dd_metrics`.)
 */
export function ddRichExtractionOf(payload: unknown): DDRichExtraction | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
  const p = payload as { richExtraction?: unknown; extraction?: unknown };
  const isObj = (v: unknown): v is DDRichExtraction => !!v && typeof v === 'object' && !Array.isArray(v);
  if (isObj(p.richExtraction)) return p.richExtraction;
  if (isObj(p.extraction)) return p.extraction;
  return undefined;
}

/** Currency formatter that handles B/M/K (the shared `formatCurrency` stops at M). */
function money(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

const isPos = (n: number | null | undefined): n is number => typeof n === 'number' && n > 0;
const isNum = (n: number | null | undefined): n is number => typeof n === 'number' && n !== 0;

export function assembleDDMetrics(
  profile: DDMetricsProfileSource | null | undefined,
  rich: DDRichExtraction | null | undefined,
): DDMetric[] {
  const metrics: DDMetric[] = [];

  const ue = rich?.data?.unitEconomics ?? undefined;
  const md = rich?.data?.marketData ?? undefined;
  const confidence = typeof rich?.quality?.overallConfidence === 'number'
    ? rich.quality.overallConfidence
    : undefined;
  const deck = (key: string, label: string, value: string): DDMetric => ({
    key,
    label,
    value,
    source: 'deck',
    ...(confidence != null ? { confidence } : {}),
  });

  // ── Profile (founder self-reported) ──
  if (profile) {
    if (isPos(profile.monthly_revenue)) {
      metrics.push({ key: 'mrr', label: 'MRR', value: `${money(profile.monthly_revenue)}/mo`, source: 'profile' });
    }
    if (isPos(profile.user_count)) {
      metrics.push({ key: 'users', label: 'Users', value: formatCompact(profile.user_count), source: 'profile' });
    }
    if (isPos(profile.growth_rate)) {
      metrics.push({ key: 'growth', label: 'Growth', value: `${profile.growth_rate}%`, source: 'profile' });
    }
    if (isPos(profile.team_size)) {
      metrics.push({ key: 'team', label: 'Team', value: `${profile.team_size}`, source: 'profile' });
    }
    if (isPos(profile.funding_target)) {
      metrics.push({ key: 'raising', label: 'Raising', value: money(profile.funding_target), source: 'profile' });
    }
  }

  // ── Deck extraction (persisted richExtraction) ──
  if (ue) {
    if (typeof ue.runway === 'string' && ue.runway.trim()) metrics.push(deck('runway', 'Runway', ue.runway.trim()));
    if (isPos(ue.burnRate)) metrics.push(deck('burn', 'Burn', `${money(ue.burnRate)}/mo`));
    if (isPos(ue.cac)) metrics.push(deck('cac', 'CAC', money(ue.cac)));
    if (isPos(ue.ltv)) metrics.push(deck('ltv', 'LTV', money(ue.ltv)));
    if (isPos(ue.ltvCacRatio)) metrics.push(deck('ltvCac', 'LTV:CAC', `${ue.ltvCacRatio.toFixed(1)}x`));
    if (isPos(ue.paybackMonths)) metrics.push(deck('payback', 'Payback', `${ue.paybackMonths} mo`));
    if (isNum(ue.grossMargin)) metrics.push(deck('grossMargin', 'Gross margin', `${ue.grossMargin}%`));
    if (isNum(ue.netMargin)) metrics.push(deck('netMargin', 'Net margin', `${ue.netMargin}%`));
  }

  // Market: prefer the deck's extracted TAM; fall back to the founder's profile estimate.
  if (md && isPos(md.tam)) {
    metrics.push(deck('tam', 'TAM', money(md.tam)));
  } else if (isPos(profile?.market_size)) {
    metrics.push({ key: 'market', label: 'Market', value: money(profile!.market_size!), source: 'profile' });
  }
  if (md) {
    if (isPos(md.sam)) metrics.push(deck('sam', 'SAM', money(md.sam)));
    if (isPos(md.som)) metrics.push(deck('som', 'SOM', money(md.som)));
    if (isPos(md.marketGrowthRate)) metrics.push(deck('marketGrowth', 'Market growth', `${md.marketGrowthRate}%`));
  }

  return metrics;
}
